import React, { useEffect, useRef, useState } from 'react';
import GACHA_CSS from './gachaStyles';
import GachaMachine from './GachaMachine';
import WelcomeScreen from './WelcomeScreen';
import NamesScreen from './NamesScreen';
import NoteModal from './NoteModal';
import HistoryPanel from './HistoryPanel';
import {
  DRAW_PALETTE,
  MAX_NAMES,
  LID_COLOR,
  LID_HI,
  clearSession,
  drawQuestion,
  genBalls,
  loadSession,
  qNo,
  saveSession,
  startTracking,
  track,
} from './gachaData';
import {
  createIcebreakerDraw,
  markIcebreakerAnswered,
  markIcebreakerSkipped,
} from '../../api/icebreaker/icebreaker.api';

const HINTS = {
  cranking: 'Round and round…',
  dropping: 'Here it comes…',
  ready: 'Tap the capsule',
  note: '',
  fly: '',
};

const IcebreakerGacha = () => {
  const [screen, setScreen] = useState('welcome'); // welcome | names | main
  const [names, setNames] = useState([]);
  const [nameInput, setNameInput] = useState('');
  const [turn, setTurn] = useState(0);
  // idle → cranking → dropping → ready → note → fly
  const [phase, setPhase] = useState('idle');
  const [q, setQ] = useState(null);
  const [drawColor, setDrawColor] = useState(DRAW_PALETTE[1]);
  const [history, setHistory] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [person, setPerson] = useState(null);
  const [badgePop, setBadgePop] = useState(false);
  const [balls] = useState(genBalls);

  const timersRef = useRef([]);
  const addTimer = (fn, ms) => timersRef.current.push(setTimeout(fn, ms));

  // 현재 뽑기의 DB 기록 요청. 응답이 늦어도 스킵/답변 처리가 따라가도록
  // id 대신 Promise를 들고 있는다. 실패 시 null(기록만 유실, 게임은 계속).
  const drawReqRef = useRef(null);

  useEffect(() => {
    const s = loadSession();
    if (s && s.names && s.names.length) {
      setNames(s.names);
      setTurn(s.turn || 0);
      setHistory(s.history || []);
      setSkipped(s.skipped || []);
      setScreen('main');
    }
    const timers = timersRef.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  const cur = names[turn] || '';

  const addName = () => {
    const n = nameInput.trim();
    if (!n || names.includes(n) || names.length >= MAX_NAMES) {
      setNameInput('');
      return;
    }
    setNames([...names, n]);
    setNameInput('');
  };

  const startSession = () => {
    if (names.length < 2) return;
    setScreen('main');
    setTurn(0);
    saveSession({ names, turn: 0, history, skipped });
    startTracking(names);
  };

  const crank = () => {
    if (phase !== 'idle') return;
    const nq = drawQuestion({ history, skipped, currentName: cur, exclude: null });
    const color = DRAW_PALETTE[Math.floor(Math.random() * DRAW_PALETTE.length)];
    track('draw', cur, nq);
    drawReqRef.current = createIcebreakerDraw(cur, qNo(nq), nq).catch(() => null);
    setQ(nq);
    setDrawColor(color);
    setPhase('cranking');
    addTimer(() => setPhase('dropping'), 620);
    addTimer(() => setPhase('ready'), 1650);
  };

  const redraw = () => {
    const nextSkipped = [...skipped, { name: cur, q, t: Date.now() }];
    const nq = drawQuestion({
      history,
      skipped: nextSkipped,
      currentName: cur,
      exclude: q,
    });
    track('skip', cur, nq);
    const prevReq = drawReqRef.current;
    if (prevReq) {
      prevReq.then((d) => d && markIcebreakerSkipped(d.id).catch(() => {}));
    }
    drawReqRef.current = createIcebreakerDraw(cur, qNo(nq), nq).catch(() => null);
    setQ(nq);
    setSkipped(nextSkipped);
    saveSession({ names, turn, history, skipped: nextSkipped });
  };

  const done = () => {
    if (phase !== 'note') return;
    track('complete', cur, q);
    const req = drawReqRef.current;
    drawReqRef.current = null;
    if (req) {
      req.then((d) => d && markIcebreakerAnswered(d.id).catch(() => {}));
    }
    setPhase('fly');
    addTimer(() => {
      const nextHistory = [...history, { name: cur, q, t: Date.now() }];
      const nextTurn = (turn + 1) % Math.max(1, names.length);
      setHistory(nextHistory);
      setTurn(nextTurn);
      setPhase('idle');
      setQ(null);
      setBadgePop(true);
      saveSession({ names, turn: nextTurn, history: nextHistory, skipped });
      addTimer(() => setBadgePop(false), 700);
    }, 800);
  };

  const resetAll = () => {
    if (!window.confirm('Start over? Names and history will be cleared.')) {
      return;
    }
    clearSession();
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setScreen('welcome');
    setNames([]);
    setNameInput('');
    setTurn(0);
    setPhase('idle');
    setQ(null);
    setHistory([]);
    setSkipped([]);
    setHistOpen(false);
    setPerson(null);
  };

  const pickTurn = (i) => {
    if (phase !== 'idle' || turn === i) return;
    setTurn(i);
    saveSession({ names, turn: i, history, skipped });
  };

  const hint = phase === 'idle' ? (cur ? `${cur}, pull the crank` : '') : HINTS[phase] || '';

  return (
    <div
      className='ibg-root'
      style={{
        minHeight: '100svh',
        background:
          'radial-gradient(120% 90% at 50% 0%, var(--c-n-100) 0%, var(--c-bg) 55%, var(--c-surface) 100%)',
        color: 'var(--c-text)',
        fontFamily: 'var(--f-body)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{GACHA_CSS}</style>

      {screen === 'welcome' && (
        <WelcomeScreen onBegin={() => setScreen('names')} />
      )}

      {screen === 'names' && (
        <NamesScreen
          names={names}
          nameInput={nameInput}
          onNameInput={setNameInput}
          onAddName={addName}
          onRemoveName={(i) => setNames(names.filter((_, j) => j !== i))}
          onStart={startSession}
        />
      )}

      {screen === 'main' && (
        <>
          <div
            style={{
              minHeight: '100svh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'clamp(14px, 2.6vh, 30px)',
              padding: 'clamp(16px, 4vw, 40px)',
            }}
          >
            <div
              style={{
                position: 'fixed',
                top: 'clamp(14px, 3vw, 24px)',
                left: 'clamp(16px, 3vw, 28px)',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.2em',
                color: 'var(--c-n-600)',
                zIndex: 10,
              }}
            >
              KOREADY · LANGUAGE EXCHANGE
            </div>

            {/* 참가자 레일 */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                marginTop: 'clamp(28px, 5vh, 10px)',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.26em',
                  color: 'var(--c-warm-700)',
                }}
              >
                NOW DRAWING
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: '8px clamp(16px, 3vw, 26px)',
                  maxWidth: 760,
                }}
              >
                {names.map((name, i) => (
                  <div
                    key={name}
                    onClick={() => pickTurn(i)}
                    title='Make it their turn'
                    className='ibg-rail-item'
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: 'var(--c-accent)',
                        opacity: i === turn ? 1 : 0,
                        transition: 'opacity 0.4s',
                      }}
                    />
                    <div
                      style={{
                        fontSize: 'clamp(15px, 2vw, 18px)',
                        fontWeight: i === turn ? 700 : 500,
                        color: 'var(--c-text)',
                        opacity: i === turn ? 1 : 0.32,
                        transition: 'opacity 0.45s',
                        letterSpacing: '0.01em',
                      }}
                    >
                      {name}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <GachaMachine
              phase={phase}
              drawColor={drawColor}
              balls={balls}
              onCrank={crank}
              onCapsuleClick={() => {
                if (phase === 'ready') setPhase('note');
              }}
            />

            <div
              style={{
                minHeight: 24,
                fontFamily: 'var(--f-heading)',
                fontSize: 'clamp(16px, 2.4vw, 20px)',
                color: 'var(--c-n-700)',
                textAlign: 'center',
                transition: 'opacity 0.3s',
              }}
            >
              {hint}
            </div>
          </div>

          {/* 히스토리 박스 */}
          <div
            onClick={() => {
              setHistOpen(true);
              setPerson(null);
            }}
            className='ibg-history-fab'
            style={{
              position: 'fixed',
              right: 'clamp(16px, 3vw, 30px)',
              bottom: 'clamp(16px, 3vw, 30px)',
              zIndex: 30,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 7,
            }}
          >
            <div style={{ position: 'relative', width: 62, height: 44 }}>
              <div
                style={{
                  position: 'absolute',
                  inset: '6px 0 0 0',
                  background:
                    'linear-gradient(180deg, var(--c-n-200), var(--c-n-300))',
                  borderRadius: 12,
                  boxShadow: '0 10px 24px rgba(90, 60, 40, 0.25)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: -4,
                  right: -4,
                  height: 13,
                  background: `linear-gradient(180deg, ${LID_HI}, ${LID_COLOR} 75%)`,
                  borderRadius: 999,
                  boxShadow: '0 3px 6px rgba(61, 71, 43, 0.35)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 22,
                  right: 22,
                  height: 4,
                  borderRadius: 2,
                  background: 'rgba(0,0,0,0.3)',
                }}
              />
              {history.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: -12,
                    right: -12,
                    minWidth: 22,
                    height: 22,
                    borderRadius: 11,
                    background: 'var(--c-accent)',
                    color: 'var(--c-n-100)',
                    fontSize: 12,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 6px',
                    boxShadow: '0 4px 8px rgba(86, 99, 63, 0.4)',
                    animation: badgePop
                      ? 'ibgBadgePop 0.5s cubic-bezier(0.2, 1.4, 0.4, 1) both'
                      : 'none',
                  }}
                >
                  {history.length}
                </div>
              )}
            </div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.18em',
                color: 'var(--c-n-600)',
              }}
            >
              HISTORY
            </div>
          </div>
        </>
      )}

      {phase === 'note' && (
        <NoteModal
          name={cur}
          question={q}
          onClose={() => setPhase('ready')}
          onRedraw={redraw}
          onDone={done}
        />
      )}

      {/* 답변 완료 후 노트가 히스토리 박스로 날아가는 연출 */}
      {phase === 'fly' && (
        <div
          className='ibg-overlay'
          style={{
            position: 'fixed',
            top: '46%',
            left: '50%',
            width: 150,
            height: 110,
            marginLeft: -75,
            background:
              'linear-gradient(175deg, var(--c-n-100), var(--c-n-200))',
            borderRadius: 14,
            boxShadow: '0 18px 44px rgba(46, 43, 37, 0.35)',
            zIndex: 45,
            animation: 'ibgFlyFold 0.8s cubic-bezier(0.45, 0, 0.75, 0.35) both',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 14,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--f-heading)',
              fontSize: 12,
              lineHeight: 1.3,
              color: 'var(--c-n-600)',
              textAlign: 'center',
              overflow: 'hidden',
            }}
          >
            {q}
          </div>
        </div>
      )}

      {histOpen && (
        <HistoryPanel
          names={names}
          history={history}
          person={person}
          onPickPerson={setPerson}
          onBack={() => setPerson(null)}
          onClose={() => {
            setHistOpen(false);
            setPerson(null);
          }}
          onReset={resetAll}
        />
      )}
    </div>
  );
};

export default IcebreakerGacha;
